import React, {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import Loading from "shared/ui/Loading";
import axios from "axios";
import {authInterceptor} from "../../../../api/apiClient";
import Button from "@mui/material/Button";
import {useReactToPrint} from "react-to-print";
import {APIPostExternalFinishTest, APIPostFinishTest} from "../api/answerReport";
import ReportHeader from "../../shared/components/ReportHeader";
import AnswersMenu from "../components/AnswersMenu";
import Typography from "@mui/material/Typography";

type TestMode = { id: number };
type StudentAnswers = { test_mode: TestMode,  [key: string]: any; } & Record<string, unknown>;

const deriveLocationInfo = (pathname: string) => {
    const parts = pathname.split("/").filter(Boolean);
    const contextPath = parts[parts.length - 2] ?? "";
    const archivedLocation = parts[parts.length - 3] ?? "";
    return {contextPath, isArchived: archivedLocation === "istoric-teste"};
};

const StudentAnswersReport = () => {
    const {id = ""} = useParams();
    const location = useLocation();
    const {update} = useAuth();
    const [loading, setLoading] = useState<boolean>(true);
    const [studentAnswers, setStudentAnswers] = useState<StudentAnswers | null>(null);
    const [locationUrl, setLocationUrl] = useState<string>("");
    const reportPrintRef = useRef(null);

    const [searchParams] = useSearchParams();
    const payload = searchParams.get("payload");

    const {isArchived, contextPath: derivedContextPath} = useMemo(
        () => deriveLocationInfo(location.pathname),
        [location.pathname]
    );

    useEffect(() => {
        if (id !== "") {
            setLoading(true);
            if (payload !== null) {
                setLocationUrl("raspunsuri");
                APIPostExternalFinishTest({payload: payload})
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("PostFinishTest OK", response);
                            setStudentAnswers(response.data.data);
                            setLoading(false);
                        } else {
                            update("error", {show: true, code: "A002"});
                        }
                    })
                    .catch(() => {
                        update("error", {show: true, code: "A001"});
                    })
                    .finally(() => {
                        axios.interceptors.request.eject(authInterceptor);
                    });
            } else {
                setLocationUrl(derivedContextPath);
                APIPostFinishTest({test: id, isArchived: isArchived})
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("PostFinishTest OK", response);
                            setStudentAnswers(response.data.data);
                        } else {
                            update("error", {show: true, code: "A002"});
                        }
                    })
                    .catch(() => {
                        // console.log("PostFinishTest ERR", err);
                        update("error", {show: true, code: "A001"});
                    })
                    .finally(() => { setLoading(false);})
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, payload, isArchived, derivedContextPath]);

    const handlePrint = useReactToPrint({
            content: () => reportPrintRef.current,
        });

    return (
        <>
            <div ref={reportPrintRef} className="print">
                <Box component="main">
                    {loading ? (
                        <Loading show={true}/>
                    ) : (
                        studentAnswers &&
                        <>
                            <ReportHeader
                                locationUrl={locationUrl}
                                reportId={id}
                                score={studentAnswers.score}
                                testDate={studentAnswers.started_at}
                                testTypeId={studentAnswers.test_mode.id}
                                subjectName={studentAnswers.discipline}
                                className={studentAnswers.grade_level}
                                isArchived={studentAnswers.is_archived}
                            />

                            <Typography variant="h1">Itemi</Typography>

                            <AnswersMenu
                                items={studentAnswers.items}
                                testModeId={studentAnswers.test_mode.id}
                                locationUrl={locationUrl}
                                testId={id}
                                totalItems={studentAnswers.total_items}
                                noAnswers={studentAnswers.no_answers ?? studentAnswers.total_no_answers}
                                rightAnswers={studentAnswers.right_answers ?? studentAnswers.total_right_answers}
                                wrongAnswers={studentAnswers.wrong_answers ?? studentAnswers.total_wrong_answers}
                                notPayingAttention={studentAnswers.not_paying_attention ?? studentAnswers.total_not_paying_attention}
                                isPrinting={true}
                            />
                        </>
                    )}
                </Box>
            </div>
                <Box component="main">
                    {loading ? (
                        <Loading show={true}/>
                    ) : (
                        studentAnswers &&
                            <>
                                <ReportHeader
                                    locationUrl={locationUrl}
                                    reportId={id}
                                    score={studentAnswers.score}
                                    testDate={studentAnswers.started_at}
                                    testTypeId={studentAnswers.test_mode.id}
                                    subjectName={studentAnswers.discipline}
                                    className={studentAnswers.grade_level}
                                    isArchived={studentAnswers.is_archived}
                                />

                                <Typography variant="h1" my={2}>Itemi</Typography>

                                <AnswersMenu
                                    items={studentAnswers.items}
                                    testModeId={studentAnswers.test_mode.id}
                                    locationUrl={locationUrl}
                                    testId={id}
                                    totalItems={studentAnswers.total_items}
                                    noAnswers={studentAnswers.no_answers ?? studentAnswers.total_no_answers}
                                    rightAnswers={studentAnswers.right_answers ?? studentAnswers.total_right_answers}
                                    wrongAnswers={studentAnswers.wrong_answers ?? studentAnswers.total_wrong_answers}
                                    notPayingAttention={studentAnswers.not_paying_attention ?? studentAnswers.total_not_paying_attention}
                                    isPrinting={false}
                                />

                                <div className="print-bar screen no-print">
                                    <Button type="submit" variant="contained" onClick={handlePrint} style={{marginRight: 20}}>
                                        Tipărește
                                    </Button>
                                </div>
                            </>
                    )}
                </Box>
        </>
    );
};

export default StudentAnswersReport;
